// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import axios from 'axios';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = (marketplaceRootURL) => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running on a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Safely handle the case when window is not defined
  if (typeof window === 'undefined') {
    // Fallback to marketplaceRootURL or an empty string
    return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : rootUrl;
  }

  // Use the given marketplaceRootURL or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : `${window.location.origin}`;
};


// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const apiBase = apiBaseUrl();
  const url = `${apiBase}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // Determine if the body should be serialized as transit format
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') &&
    body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader
      ? contentTypeHeader.split(';')[0]
      : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }

    return res.text();
  });
};


// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};


// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getExchangeRates = () => {
  return post('/api/stored-exchange-rates', {});
};

export const userActiveListings = userId => {
  return post('/api/current-user-listings-count/published', { userId });
};

export const currentUserListingsClosed = userId => {
  return post('/api/current-user-listings-count/closed', { userId });
};

export const currentUserTransactionsTotals = () => {
  return post('/api/current-user-transactions-total', {});
};

//Update listing rating, when customer reviews
export const updateAuthorRating = ({ authorId, rating }) => {
  return post('/api/author/updateRating', { authorId, rating });
};

// Algolia
export const createAlgoliaData = body => {
  return post('/api/createAlgoliaData', body);
};

export const updateAlgoliaData = body => {
  return post('/api/updateAlgoliaData', body)
}

export const deleteAlgoliaData = body => {
  return post('/api/deleteAlgoliaData', body);
};

export const updateAlgoliaViews = ({ indexName, recordIds }) => {
  return post('/api/updateAlgoliaViews', { indexName, recordIds })
};

export const updaeAlgoliaListingsUser = (userId) => {
  return post('/api/updaeAlgoliaListingsUser', { userId })
}


// Cloudinary

export const deleteImage = ({ publicId }) => {
  // console.log('public', publicId);
  return post('/api/deleteImage', { publicId });
};

// get user details for generating reviews
export const getUserAchievements = (userId) => {
  return post('/api/user-achievements', { userId });
}

export const replicateListing = ({ listingId, title, description, currentUserId }) => {
  return post('/api/replicate-listing', { listingId, title, description, currentUserId })
};

export const fetchAlgoliaHits = (data) => {
  return post('/api/fetch-algolia-hits', data)
}

export const sendEmailToSearchTeam = (data) => {
  return post('/api/send-email-to-search-team', data)
}
// Send Grid mails
export const sendSchedularMailFromSendGrid = (data) => {
  return post('/api/send-schedular-mail', data)
}
export const cancelUpcomingMail = (data) => {
  return post('/api/cancel-upcoming-mail', data)
}

export const getCloudinarySignature = ({ public_id, transformation }) => {
  return post('/api/generate-signature', { public_id, transformation })
}

export const queryAlgolia = (body) => {
  return post('/api/query-algolia', body);
}

export const fetchUserCountry = async () => {
  const response = await fetch('https://ipcheck.icodestaging.workers.dev');
  const vr1 = await response.text();
  return vr1.split('User Country Code: ')?.[1] ?? '';
};

export const getLocationWithMapBox = async (longitude, latitude) => {
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`
  );
  return await response.json();
}

export const boostAvailability = (body) => {
  return post('/api/update-listing-availability', body);
}

// Micro-Service API Calls
const microServiceBaseURL = process.env.GENERATIVE_MICRO_SERVICE_URL || 'https://rasmicroservice.onrender.com'
const axiosMicroService = axios.create({
  baseURL: microServiceBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Listing Generation API's
export const generateAIListing = (body) => {
  return axiosMicroService.post('/generate/listing/content', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const getComplementaryItems = body => {
  return axiosMicroService.post('/generate/listing/complementary-items', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const enhanceField = (body) => {
  return axiosMicroService.post('/generate/listing/enhance-field', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const generateAIImages = body =>{
  return axiosMicroService.post('/generate/listing/images', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const getCredits = (token) => {
  return axiosMicroService.get('/ai/credits', { params: { listingToken: token } })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};


// Blog Generation API's
// Blog titles with labels
export const generateBlogTitleswithLabels = body => {
  return axiosMicroService.post('/generate/blog/titles', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const generateBlogs = body => {
  return axiosMicroService.post('/generate/blog/content', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};


// Blog CRUD APIs
export const createBlogPost = body => {
  return axiosMicroService.post('/blogs', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
}

export const getBlogsByListingId = listingId => {
  return axiosMicroService.get(`/blogs/listing/${listingId}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const updateBlogPost = (id, update) => {
  return axiosMicroService.put(`/blogs/${id}`, {...update })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const deleteBlogPost = id => {
  return axiosMicroService.delete(`/blogs/${id}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};

export const publishBlogPost = id => {
  return axiosMicroService.put(`/blogs/${id}`, { id, status: 'published' })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
}

// Fetch a single blog by its ID
export const getBlogPost = id => {
  return axiosMicroService.get(`/blogs/${id}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error(error);
      throw error;
    });
};